import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { PlusIcon } from '@phosphor-icons/react';

import { orpc } from '@/api/rpc/client';
import { ForegroundAgentChat } from '@/components/common/ForegroundAgentChat';
import GentlyTable, { type TableColumn } from '@/components/common/GentlyTable';
import { CustomersAutocomplete } from '@/components/customer/CustomersAutocomplete';
import { CustomerPill } from '@/components/customer/CustomerPill';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAgentPanel } from '@/context/agent-panel.context';
import paths from '@/paths';
import Page from '@/components/page/Page';
import type { Portal, PortalStatus } from '@erp/rpc';

type StatusFilter = 'all' | PortalStatus;

const dateFormatter = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  timeZone: 'UTC',
});

function formatDate(date: Date) {
  return dateFormatter.format(date);
}

function statusLabel(status: PortalStatus) {
  switch (status) {
    case 'draft':
      return 'Draft';
    case 'published':
      return 'Published';
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

export function PortalsPage() {
  const navigate = useNavigate();
  const { open: openAgentPanel } = useAgentPanel();

  const [customerId, setCustomerId] = useState<string | undefined>();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const listInput = useMemo(
    () => ({
      customerId,
      status: statusFilter === 'all' ? undefined : statusFilter,
      limit: 100,
    }),
    [customerId, statusFilter],
  );

  const { data, isLoading, error } = useQuery(
    orpc.portal.list.queryOptions({ input: listInput }),
  );

  const portals = data?.portals ?? [];

  const columns: TableColumn<Portal>[] = useMemo(
    () => [
      {
        id: 'name',
        label: 'Name',
        sortable: true,
      },
      {
        id: 'customer',
        label: 'Customer',
        sortable: false,
        render: (value) =>
          value?.name ? (
            <CustomerPill customer={{ name: value.name }} />
          ) : (
            <span className="text-sm text-muted-foreground">-</span>
          ),
      },
      {
        id: 'status',
        label: 'Status',
        sortable: true,
        render: (value) => (
          <Badge variant={value === 'published' ? 'success' : 'warning'}>
            {statusLabel(value)}
          </Badge>
        ),
      },
      {
        id: 'createdAt',
        label: 'Created',
        sortable: true,
        render: (value) => <span className="text-sm">{formatDate(value)}</span>,
      },
      {
        id: 'updatedAt',
        label: 'Updated',
        sortable: true,
        render: (value) => <span className="text-sm">{formatDate(value)}</span>,
      },
    ],
    [],
  );

  const hasActiveFilters = Boolean(customerId) || statusFilter !== 'all';

  function handleRowClick(portal: Portal) {
    void navigate({
      to: paths.authenticated.portal,
      params: { portalId: portal.id },
    });
  }

  function handleCreatePortal() {
    openAgentPanel();
  }

  return (
    <>
      <Page isLoading={isLoading} error={error ?? undefined}>
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl font-bold">Portals</CardTitle>
            <CardAction>
              <Button onClick={handleCreatePortal}>
                <PlusIcon className="mr-2 h-4 w-4" />
                Create Portal
              </Button>
            </CardAction>
          </CardHeader>
          <CardContent>
            <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
              <div className="flex flex-wrap items-end gap-3">
                <div className="w-[260px] space-y-1.5">
                  <Label className="text-xs text-muted-foreground">
                    Customer
                  </Label>
                  <CustomersAutocomplete
                    selectedCustomerId={customerId}
                    onCustomerSelect={setCustomerId}
                    allowClear
                    className="w-full"
                  />
                </div>
                <div className="w-[200px] space-y-1.5">
                  <Label className="text-xs text-muted-foreground">
                    Status
                  </Label>
                  <Select
                    value={statusFilter}
                    onValueChange={(v) => setStatusFilter(v as StatusFilter)}
                  >
                    <SelectTrigger className="w-full" size="default">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="published">Published</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {hasActiveFilters && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setCustomerId(undefined);
                    setStatusFilter('all');
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </div>

            <GentlyTable<Portal>
              data={portals}
              columns={columns}
              isLoading={isLoading}
              searchPlaceholder="Search portals..."
              searchFields={['name', 'slug']}
              onRowClick={handleRowClick}
              emptyMessage="No portals found"
              showSearch
              showCount
              downloadFilename="portals"
              defaultSort={{ sortBy: 'updatedAt', sortDirection: 'desc' }}
            />
          </CardContent>
        </Card>
      </Page>

      <ForegroundAgentChat
        moduleKey="portal"
        currentPage="/portals"
        hideFloatingButton
        onNavigate={(path, options) =>
          navigate({
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            to: path as any,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            search: options?.search as any,
          })
        }
      />
    </>
  );
}
